import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";

/**
 * Does a dialog or drawer hold typed input the person has not saved? Answered from the DOM, so every dialog gets it
 * without per-screen wiring: each form control's value is remembered when the layer opens (and, for controls that
 * appear later, when first focused), and it is dirty when any still-mounted control now differs. A control changed
 * without ever having been seen (a checkbox toggled through its label) counts as dirty.
 *
 * Not counted: buttons, hidden inputs, `type="search"` lookup boxes (a query is not content), and anything inside
 * `[data-dirty-ignore]`. Inside a `[data-dirty-proxy]` widget only its `input[data-dirty-value]` mirror counts (GRF-19:
 * the people picker's text box is a search; the person chosen is the value). A screen whose unsaved state is not in a control (e.g. a restored transition draft) says so
 * with the `unsaved` prop of Dialog.
 */
type Control = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLElement;

const IGNORED_INPUT_TYPES = new Set(["button", "submit", "reset", "hidden", "image", "search"]);
const EDITABLE = '[contenteditable="true"], [contenteditable=""]';

function controlOf(node: EventTarget | null): Control | null {
  if (!(node instanceof HTMLElement) || node.closest("[data-dirty-ignore]")) return null;
  // A composite widget (the people picker) stands for its value with a mirror input: anything inside it counts as that.
  const proxy = node.closest("[data-dirty-proxy]");
  if (proxy) return proxy.querySelector<HTMLInputElement>("input[data-dirty-value]");
  if (node instanceof HTMLInputElement) return IGNORED_INPUT_TYPES.has(node.type) ? null : node;
  if (node instanceof HTMLTextAreaElement || node instanceof HTMLSelectElement) return node;
  const editable = node.closest<HTMLElement>(EDITABLE);
  return editable && !editable.parentElement?.closest(EDITABLE) ? editable : null;
}

function valueOf(control: Control): string {
  if (control instanceof HTMLInputElement) {
    if (control.type === "checkbox" || control.type === "radio") return String(control.checked);
    if (control.type === "file") return String(control.files?.length ?? 0);
    return control.value;
  }
  if (control instanceof HTMLSelectElement) {
    return Array.from(control.selectedOptions, (option) => option.value).join("\u0000");
  }
  if (control instanceof HTMLTextAreaElement) return control.value;
  return control.textContent ?? "";
}

export interface UnsavedInputTracker {
  /** Forget everything and remember the current value of every control now inside the element. */
  reset: () => void;
  isDirty: () => boolean;
}

/** Listens while `active` (the element must be mounted by then). */
export function useUnsavedInput(ref: RefObject<HTMLElement | null>, active: boolean): UnsavedInputTracker {
  const baseline = useRef(new Map<Control, string>());
  const unseen = useRef(new Set<Control>());

  useEffect(() => {
    const root = ref.current;
    if (!active || !root) return undefined;
    function remember(event: Event) {
      const control = controlOf(event.target);
      if (control && !baseline.current.has(control)) baseline.current.set(control, valueOf(control));
    }
    function changed(event: Event) {
      const control = controlOf(event.target);
      if (control && !baseline.current.has(control)) unseen.current.add(control);
    }
    root.addEventListener("focusin", remember, true);
    root.addEventListener("input", changed, true);
    root.addEventListener("change", changed, true);
    return () => {
      root.removeEventListener("focusin", remember, true);
      root.removeEventListener("input", changed, true);
      root.removeEventListener("change", changed, true);
    };
  }, [ref, active]);

  const reset = useCallback(() => {
    baseline.current.clear();
    unseen.current.clear();
    const root = ref.current;
    if (!root) return;
    root.querySelectorAll<HTMLElement>(`input, textarea, select, ${EDITABLE}`).forEach((node) => {
      const control = controlOf(node);
      if (control && !baseline.current.has(control)) baseline.current.set(control, valueOf(control));
    });
  }, [ref]);

  const isDirty = useCallback(() => {
    for (const control of unseen.current) if (control.isConnected) return true;
    for (const [control, value] of baseline.current) if (control.isConnected && valueOf(control) !== value) return true;
    return false;
  }, []);

  return useMemo(() => ({ reset, isDirty }), [reset, isDirty]);
}
